// Generated from scripts/product-recommendations.ts. Rebuild with pnpm scripts:build.
import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
/**
 * Where a section starts loading, measured from the viewport: one viewport height before it is reached, so the answer
 * is almost always in before the shopper is, and a section that turns out to be empty leaves before anyone sees it.
 */
const LOAD_MARGIN = '0px 0px 100% 0px';
/** The answers received, by address, so a section that loads again on the same page does not ask again. */
const answers = new Map();
function isAbort(error) {
    return error instanceof Error && error.name === 'AbortError';
}
function inDesignMode() {
    return window.Shopify?.designMode === true;
}
/**
 * Runs `change` and, if the content the shopper is looking at moved because of it, scrolls it back. The browser's own
 * scroll anchoring does this in some browsers and not in others (Safari has none), so the result is measured instead
 * of assumed: an element at the middle of the viewport keeps its place. Content that is below the change or on
 * another part of the page does not move, and nothing is done.
 */
async function keepViewportStable(change) {
    const probe = document.elementFromPoint(window.innerWidth / 2, window.innerHeight / 2);
    const before = probe?.getBoundingClientRect().top;
    await change();
    if (!probe || before === undefined || !probe.isConnected)
        return;
    const shift = probe.getBoundingClientRect().top - before;
    if (Math.abs(shift) >= 1)
        window.scrollBy({ top: shift, behavior: 'instant' });
}
/**
 * The product recommendations section (and block): it asks Shopify for the recommendations when it nears the viewport
 * and swaps its placeholder for the answer.
 *
 * Shopify answers the Product Recommendations endpoint (`routes.product_recommendations_url` with `product_id`,
 * `limit`, `section_id` and `intent`, where `intent` is `related` or `complementary`) with the section rendered with
 * `recommendations.performed` true. The answer replaces only the keyed products part inside this element (its
 * `data-hydration-key` wrapper) through the section renderer's hydration morph. The heading blocks the merchant composed
 * sit outside it and stay as the page rendered them: the endpoint renders the saved settings, so a heading edited in the
 * theme editor would otherwise be swapped back for the saved one. For the same reason the key names the layout, and an
 * answer whose key differs (a layout changed in the editor and not yet saved) is not shown.
 *
 * Empty is a result, not an error: a product with no recommendations (a product with no complementary products
 * configured, a network failure) leaves the storefront with no section at all. `data-removes="section"` removes the
 * whole `.shopify-section`, otherwise only this element, and no reserved space is left. The theme editor keeps
 * the section so the merchant can select it, and the server renders a note in its place.
 *
 * The page keeps still while this happens. The placeholder reserves the height of the final list; and when the swap or
 * the removal happens above the viewport, the content the shopper is reading is scrolled back to where it was.
 *
 * Contract kept from the inherited module: the `product-recommendations` element and its `data-url`,
 * `data-section-id`, `data-product-id`, `data-intent` and `data-recommendations-performed` attributes, the section
 * renderer's hydration key, and a reload when those attributes change before the answer has landed (a combined
 * listing replacing the page). It loads when the element is near the viewport. A section the server already rendered
 * with its answer (`data-recommendations-performed="true"`, as in the editor preview) is left as it is.
 */
class ProductRecommendations extends Component {
    static observedAttributes = ['data-product-id', 'data-intent', 'data-url', 'data-recommendations-performed'];
    #observer;
    #request;
    #connected = false;
    connectedCallback() {
        super.connectedCallback();
        this.#connected = true;
        this.#watch();
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.#connected = false;
        this.#observer?.disconnect();
        this.#observer = undefined;
        this.#request?.abort();
        this.#request = undefined;
    }
    /** A new product or intent before the answer has landed means a new question. */
    attributeChangedCallback(_name, previous, next) {
        if (!this.#connected || previous === next)
            return;
        if (this.dataset.recommendationsPerformed === 'true')
            return;
        this.#watch();
    }
    /** Waits until the element is near the viewport, then asks once. */
    #watch() {
        this.#observer?.disconnect();
        this.#request?.abort();
        if (this.dataset.recommendationsPerformed === 'true')
            return;
        const observer = new IntersectionObserver((entries) => {
            if (!entries.some((entry) => entry.isIntersecting))
                return;
            observer.disconnect();
            void this.#load();
        }, { rootMargin: LOAD_MARGIN });
        this.#observer = observer;
        observer.observe(this);
    }
    /** The address of the question, or undefined when the element does not say enough to ask it. */
    get #endpoint() {
        const { url, productId, sectionId, intent } = this.dataset;
        if (!url || !productId || !sectionId)
            return undefined;
        const endpoint = new URL(url, window.location.href);
        endpoint.searchParams.set('product_id', productId);
        endpoint.searchParams.set('section_id', sectionId);
        if (intent)
            endpoint.searchParams.set('intent', intent);
        return endpoint;
    }
    async #load() {
        const endpoint = this.#endpoint;
        const { sectionId } = this.dataset;
        if (!endpoint || !sectionId)
            return;
        const controller = new AbortController();
        this.#request = controller;
        let answer = answers.get(endpoint.href);
        if (answer === undefined) {
            try {
                const response = await fetch(endpoint, { signal: controller.signal });
                if (!response.ok) {
                    this.#give();
                    return;
                }
                answer = await response.text();
            }
            catch (error) {
                if (!isAbort(error))
                    this.#give();
                return;
            }
            if (!inDesignMode())
                answers.set(endpoint.href, answer);
        }
        if (controller.signal.aborted || !this.isConnected)
            return;
        const page = new DOMParser().parseFromString(answer, 'text/html');
        const next = page.getElementById(this.id);
        if (!next) {
            // The editor places sections that the renderer does not know; the storefront has asked for one it has.
            this.#give();
            return;
        }
        // The keyed part names the layout. The endpoint renders the saved settings, so in the editor an answer for a layout
        // the merchant has just changed away from has another key: the placeholder stays until the change is saved.
        const key = this.querySelector('[data-hydration-key]')?.getAttribute('data-hydration-key');
        if (!key || !next.querySelector(`[data-hydration-key="${CSS.escape(key)}"]`)) {
            this.#give();
            return;
        }
        // The block marks its inner list, the section marks the element itself.
        const hasRecommendations = next.dataset.hasRecommendations === 'true' || next.querySelector('[data-has-recommendations="true"]') !== null;
        if (!hasRecommendations && !inDesignMode()) {
            this.#vanish();
            return;
        }
        // A carousel with its arrows at the top end of the heading carries the heading in the keyed part, so the answer
        // brings the saved heading. In the editor the page's heading may be newer: it is put back after the swap.
        const selector = `[data-hydration-key="${CSS.escape(key)}"]`;
        const heading = inDesignMode() ? this.querySelector(`${selector} [data-recommendations-heading]`)?.cloneNode(true) : undefined;
        try {
            await keepViewportStable(() => morphSection(sectionId, answer, { mode: 'hydration', injectStylesheet: true }));
        }
        catch (error) {
            console.warn('[product-recommendations] The recommendations could not be shown:', error);
            this.#give();
            return;
        }
        if (heading instanceof Element) {
            const keyed = this.querySelector(selector);
            const answered = keyed?.querySelector('[data-recommendations-heading]');
            // An empty answer has no carousel to hold the heading; it goes back above the keyed part, where the section puts it.
            if (answered)
                answered.replaceWith(heading);
            else
                keyed?.before(heading);
        }
        this.removeAttribute('aria-busy');
        this.dataset.recommendationsPerformed = 'true';
    }
    /** The question went unanswered. The storefront shows nothing; the editor, which cannot always ask, leaves the placeholder. */
    #give() {
        if (!inDesignMode())
            this.#vanish();
    }
    /** Takes the section off the page without a jump in what the shopper is reading. */
    #vanish() {
        const target = this.dataset.removes === 'section' ? (this.closest('.shopify-section') ?? this) : this;
        void keepViewportStable(() => target.remove());
    }
}
if (!customElements.get('product-recommendations')) {
    customElements.define('product-recommendations', ProductRecommendations);
}
